import { zodResolver } from '@hookform/resolvers/zod';
import { BeneficiaryAccountType, BeneficiaryCountry, Currency } from '@seapoint/server/enums';
import { isValidIBAN } from 'ibantools';
import { useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { AvatarNameDescription } from '../../../../design-system/avatar/avatar-name-description/avatar-name-description';
import { Button } from '../../../../design-system/button/button';
import { Form } from '../../../../design-system/form/form-primitives';
import { Inline } from '../../../../design-system/inline/inline';
import { Select } from '../../../../design-system/select/select';
import { Stack } from '../../../../design-system/stack/stack';
import { supportedCountryCurrencies } from '../../../../types/supported-country-currency';
import { cn } from '../../../../utils/cn';
import { getCurrencyDetails } from '../../../../utils/get-currency-details';
import { PaymentInputField } from '../../components/payment-input-field';
import { getBeneficiaryCountryDetails } from '../../utils/get-beneficiary-country-details';
import { BeneficiarySelectField } from './beneficiary-select-field';
import { BeneficiaryTypeToggle } from './beneficiary-type-toggle';

// We only allow spaces and alphanumeric characters to avoid server-side validation issues when Yapily triggers the PIS payment
const INVALID_CHARACTERS_REGEX = /[^a-zA-Z0-9\s]/;

const createFormSchema = (isBusinessBeneficiary: boolean) =>
  z
    .object({
      legalName: z
        .string()
        .trim()
        .optional()
        .refine((str) => !str || !INVALID_CHARACTERS_REGEX.test(str), {
          message: 'Legal name must only contain letters, numbers and spaces',
        })
        .superRefine((val, ctx) => {
          if (isBusinessBeneficiary && !val) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Legal name is required for business beneficiaries',
            });
          }
        }),
      firstName: z
        .string()
        .trim()
        .optional()
        .refine((str) => !str || !INVALID_CHARACTERS_REGEX.test(str), {
          message: 'First name must only contain letters, numbers and spaces',
        })
        .superRefine((val, ctx) => {
          if (!isBusinessBeneficiary && !val) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'First name is required for personal beneficiaries',
            });
          }
        }),
      lastName: z
        .string()
        .trim()
        .optional()
        .refine((str) => !str || !INVALID_CHARACTERS_REGEX.test(str), {
          message: 'Last name must only contain letters, numbers and spaces',
        })
        .superRefine((val, ctx) => {
          if (!isBusinessBeneficiary && !val) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Last name is required for personal beneficiaries',
            });
          }
        }),
      country: z.nativeEnum(BeneficiaryCountry, { message: 'Country is required' }),
      currency: z.nativeEnum(Currency, { message: 'Currency is required' }),
      iban: z
        .string()
        .trim()
        .transform((val) => val.split(' ').join(''))
        .optional(),
      accountNumber: z
        .string()
        .trim()
        .max(8, { message: 'Max 8 characters' })
        .transform((val) => val.replace(/[^0-9]/g, ''))
        .optional(),
      sortCode: z
        .string()
        .trim()
        // Accounts for the 6 digits and the 2 dashes
        .max(8, { message: 'Max 8 characters' })
        .transform((val) => val.replace(/[^0-9]/g, ''))
        .optional(),
      email: z
        .string()
        .trim()
        .optional()
        .refine((val) => !val || val === '' || z.string().email().safeParse(val).success, {
          message: 'Invalid email address',
        }),
    })
    .refine((data) => data.currency === Currency.GBP || !!data.iban, {
      message: 'IBAN is required',
      path: ['iban'],
    })
    .refine(
      (data) => {
        // Skip validation if GBP or no IBAN provided
        if (data.currency === Currency.GBP || !data.iban) {
          return true;
        }

        return isValidIBAN(data.iban);
      },
      {
        message: 'A valid IBAN is required',
        path: ['iban'],
      },
    )
    .refine((data) => data.currency !== Currency.GBP || !!data.accountNumber, {
      message: 'Account number is required',
      path: ['accountNumber'],
    })
    .refine((data) => data.currency !== Currency.GBP || !!data.sortCode, {
      message: 'Sort code is required',
      path: ['sortCode'],
    })
    .refine((data) => !isBusinessBeneficiary || !!data.legalName, {
      message: 'Legal name is required',
      path: ['legalName'],
    });

export type BeneficiaryCreationFormValues = z.infer<ReturnType<typeof createFormSchema>>;

type OnSubmitArgs = {
  detailsFormValues: BeneficiaryCreationFormValues;
  accountType: BeneficiaryAccountType;
};

type WrapperConfig =
  | {
      visible: false;
    }
  | {
      visible: true;
      label?: string;
      onSecondaryCtaClick: () => void;
    };

type CreateBeneficiaryFormProps = {
  isLoading: boolean;
  onSubmit: ({ detailsFormValues, accountType }: OnSubmitArgs) => Promise<void>;
  initialValues?: BeneficiaryCreationFormValues;
  wrapperConfig?: WrapperConfig;
};

export const CreateBeneficiaryForm = ({
  onSubmit,
  isLoading,
  initialValues,
  wrapperConfig = { visible: false },
}: CreateBeneficiaryFormProps) => {
  const [isBusinessBeneficiary, setIsBusinessBeneficiary] = useState(initialValues?.legalName ? true : false);

  const form = useForm<BeneficiaryCreationFormValues>({
    resolver: zodResolver(createFormSchema(isBusinessBeneficiary)),
    defaultValues: {
      legalName: initialValues?.legalName ?? '',
      firstName: initialValues?.firstName ?? '',
      lastName: initialValues?.lastName ?? '',
      country: initialValues?.country,
      currency: initialValues?.currency,
      iban: initialValues?.iban ?? '',
      accountNumber: initialValues?.accountNumber ?? '',
      sortCode: initialValues?.sortCode ?? '',
      email: initialValues?.email ?? '',
    },
    mode: 'onTouched',
  });

  useEffect(() => {
    // Triggers validation on first render only if initial values are provided
    if (Object.keys(initialValues ?? {}).length > 0) {
      form.trigger();
    }
  }, [form, initialValues]);

  const { dirtyFields } = form.formState;
  const beneficiaryCountryCodeList = Object.values(BeneficiaryCountry);
  const autofilledBadge = 'Autofilled from invoice';

  const handleAccountTypeChange = (isPressed: boolean) => {
    setIsBusinessBeneficiary(isPressed);

    form.setValue('legalName', '');
  };

  const handleCountryChange = (country: BeneficiaryCountry) => {
    form.setValue('country', country);

    if (country === BeneficiaryCountry.GB) {
      form.setValue('iban', undefined);

      return;
    }

    form.setValue('accountNumber', undefined);
    form.setValue('sortCode', undefined);
  };

  return (
    <FormWrapper wrapperConfig={wrapperConfig}>
      <Stack space={3} align="stretch">
        <BeneficiaryTypeToggle
          isPressed={isBusinessBeneficiary}
          onChange={handleAccountTypeChange}
          className={cn({ 'bg-seapoint-gray-background-modal': wrapperConfig.visible })}
        />

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(async (detailsFormValues) => {
              const accountType = isBusinessBeneficiary
                ? BeneficiaryAccountType.BUSINESS
                : BeneficiaryAccountType.PERSONAL;

              await onSubmit({
                detailsFormValues: {
                  ...detailsFormValues,
                  legalName: detailsFormValues.legalName ? detailsFormValues.legalName : undefined,
                  firstName: detailsFormValues.firstName ? detailsFormValues.firstName : undefined,
                  lastName: detailsFormValues.lastName ? detailsFormValues.lastName : undefined,
                  iban: detailsFormValues.iban ? detailsFormValues.iban : undefined,
                  accountNumber: detailsFormValues.accountNumber ? detailsFormValues.accountNumber : undefined,
                  sortCode: detailsFormValues.sortCode ? detailsFormValues.sortCode : undefined,
                  email: detailsFormValues.email ? detailsFormValues.email : undefined,
                },
                accountType,
              });
            })}
          >
            <Stack space={3} align="stretch">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {isBusinessBeneficiary && (
                  <div className="md:col-span-2">
                    <PaymentInputField
                      form={form}
                      label="Legal Name"
                      inputName="legalName"
                      placeholder="Legal Name"
                      badge={initialValues?.legalName && !dirtyFields.legalName ? autofilledBadge : undefined}
                      className={cn({ 'bg-seapoint-gray-background-modal': wrapperConfig.visible })}
                    />
                  </div>
                )}

                {!isBusinessBeneficiary && (
                  <>
                    <PaymentInputField
                      form={form}
                      label="First Name"
                      inputName="firstName"
                      placeholder="First Name"
                      badge={initialValues?.firstName && !dirtyFields.firstName ? autofilledBadge : undefined}
                      className={cn({ 'bg-seapoint-gray-background-modal': wrapperConfig.visible })}
                    />

                    <PaymentInputField
                      form={form}
                      label="Last Name"
                      inputName="lastName"
                      placeholder="Last Name"
                      badge={initialValues?.lastName && !dirtyFields.lastName ? autofilledBadge : undefined}
                      className={cn({ 'bg-seapoint-gray-background-modal': wrapperConfig.visible })}
                    />
                  </>
                )}

                <BeneficiarySelectField
                  form={form}
                  label="Country"
                  inputName="country"
                  placeholder="Select country"
                  onChange={handleCountryChange}
                  badge={initialValues?.country && !dirtyFields.country ? autofilledBadge : undefined}
                  className={cn({ 'bg-seapoint-gray-background-modal': wrapperConfig.visible })}
                >
                  {beneficiaryCountryCodeList.map((countryCode) => {
                    const { flag: Flag, name } = getBeneficiaryCountryDetails(countryCode);

                    return (
                      <Select.Item key={countryCode} value={countryCode} className="py-3">
                        <Inline space={2}>
                          <div className="h-6 w-6 overflow-clip rounded-full">
                            <Flag className="h-full w-full" />
                          </div>

                          <p className="text-base font-medium text-black">{name}</p>
                        </Inline>
                      </Select.Item>
                    );
                  })}
                </BeneficiarySelectField>

                <BeneficiarySelectField
                  form={form}
                  label="Currency"
                  inputName="currency"
                  placeholder="Select currency"
                  badge={initialValues?.currency && !dirtyFields.currency ? autofilledBadge : undefined}
                  className={cn({ 'bg-seapoint-gray-background-modal': wrapperConfig.visible })}
                >
                  {supportedCountryCurrencies.map((currencyCode) => {
                    const { flag, name } = getCurrencyDetails(currencyCode);
                    const currencyLabel = `${name} (${currencyCode})`;

                    const isSelected = form.watch('currency') === currencyCode;

                    return (
                      <Select.Item key={currencyCode} value={currencyCode} className="py-3">
                        <Inline space={2}>
                          <div className="h-6 w-6 overflow-clip rounded-full">
                            <img
                              src={flag}
                              alt=""
                              loading={isSelected ? 'eager' : 'lazy'}
                              fetchPriority={isSelected ? 'high' : 'low'}
                              decoding="async"
                              className="h-full w-full"
                            />
                          </div>

                          <p className="text-base font-medium text-black">{currencyLabel}</p>
                        </Inline>
                      </Select.Item>
                    );
                  })}
                </BeneficiarySelectField>
              </div>

              <AccountDetailsFields
                currency={form.watch('currency')}
                form={form}
                badge={
                  initialValues?.iban || (initialValues?.accountNumber && initialValues?.sortCode)
                    ? autofilledBadge
                    : undefined
                }
                className={cn({ 'bg-seapoint-gray-background-modal': wrapperConfig.visible })}
              />

              <PaymentInputField
                form={form}
                label="Recipient Email Address"
                inputName="email"
                isOptional
                placeholder="Enter email address"
                badge={initialValues?.email && !dirtyFields.email ? autofilledBadge : undefined}
                className={cn({ 'bg-seapoint-gray-background-modal': wrapperConfig.visible })}
              />

              <Button
                variant="default"
                size="lg"
                type="submit"
                isLoading={isLoading}
                disabled={!form.formState.isValid}
              >
                Continue
              </Button>
            </Stack>
          </form>
        </Form>
      </Stack>
    </FormWrapper>
  );
};

type AccountDetailsFieldsProps = {
  form: UseFormReturn<BeneficiaryCreationFormValues>;
  currency: Currency;
  badge?: string;
  className?: string;
};

const AccountDetailsFields = ({ currency, form, badge, className }: AccountDetailsFieldsProps) => {
  const isCurrencyGBP = currency === Currency.GBP;

  const { dirtyFields } = form.formState;

  // Sort Code and Account Number are required for GBP payments
  // https://docs.google.com/spreadsheets/d/1BLSOFh2NX3Nq0HsQ2uDRzJGJoEPkmtu5rDYoYabtvBs/edit?gid=0#gid=0
  if (isCurrencyGBP) {
    return (
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <PaymentInputField
          form={form}
          label="Account Number"
          inputName="accountNumber"
          placeholder="Account Number"
          badge={!dirtyFields.accountNumber ? badge : undefined}
          className={className}
        />

        <PaymentInputField
          form={form}
          label="Sort Code"
          inputName="sortCode"
          placeholder="Sort Code"
          badge={!dirtyFields.sortCode ? badge : undefined}
          className={className}
        />
      </div>
    );
  }

  return (
    <PaymentInputField
      form={form}
      label="IBAN"
      inputName="iban"
      placeholder="#### #### #### #### #### ##"
      badge={!dirtyFields.iban ? badge : undefined}
      className={className}
    />
  );
};

type FormWrapperProps = {
  wrapperConfig: WrapperConfig;
  children: React.ReactNode;
};

const FormWrapper = ({ children, wrapperConfig }: FormWrapperProps) => {
  if (!wrapperConfig.visible) {
    return children;
  }

  return (
    <Stack space={5} align="stretch" className="rounded-lg bg-white p-4">
      {wrapperConfig.label && (
        <AvatarNameDescription avatar={{ showAvatar: true, size: 'lg' }} name={wrapperConfig.label} />
      )}

      <Stack space={3} align="stretch">
        {children}

        <Button
          variant="link"
          size="lg"
          className="text-seapoint-blue-link"
          onClick={wrapperConfig.onSecondaryCtaClick}
        >
          Select an existing recipient instead
        </Button>
      </Stack>
    </Stack>
  );
};
